import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Check, ShieldCheck, Lock, Target, X, AlertCircle } from "lucide-react";
import SEO from "../components/SEO";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import VolunteerForm from "../components/VolunteerForm";
import MatchBanner from "../components/MatchBanner";
import PayPalCheckoutButtons from "../components/PayPalCheckoutButtons";
import StripePaymentForm from "../components/StripePaymentForm";
import { Editable } from "../components/Editable";
import { useContentApi } from "../context/ContentContext";
import { detectBrowser, isStripeRiskyBrowser } from "../lib/browser";
import { formatApiError } from "../lib/errors";
import { registerQrHitFromUrl, qrAttributionFields } from "../lib/qrAttribution";

const TIERS = [10, 25, 50, 100, 250];

const Donate = () => {
    const [searchParams] = useSearchParams();
    const urlCampaignId = searchParams.get("campaign");
    // Selected campaign — initialized from the ?campaign= URL param if present,
    // otherwise donor must pick from the dropdown before payment buttons render.
    const [campaignId, setCampaignId] = useState(urlCampaignId || "");
    const [campaign, setCampaign] = useState(null);
    const [frequency, setFrequency] = useState("one_time");
    const [amount, setAmount] = useState(50);
    const [customAmount, setCustomAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const { c } = useContentApi();

    const [form, setForm] = useState({
        donor_name: "",
        donor_email: "",
        donor_phone: "",
        message: "",
        class_tag: "",
        anonymous: false,
        tribute_type: "",
        tribute_name: "",
        tribute_notify_name: "",
        tribute_notify_email: "",
        tribute_message: "",
    });
    const [tributeOpen, setTributeOpen] = useState(false);

    useEffect(() => {
        if (campaignId) {
            api.get(`/campaigns/${campaignId}/public`)
                .then((r) => setCampaign(r.data))
                .catch(() => {});
            // QR analytics — logs a hit + stashes attribution when the URL
            // carries ?src=qr. No-op otherwise.
            registerQrHitFromUrl("campaign", campaignId);
        }
    }, [campaignId]);

    // Confirmation step state — donor must click "Review my donation" to see
    // a summary modal, then confirm before the payment buttons render. This
    // prevents accidental charges from misclicks and gives a clear "this is
    // exactly what I'm about to donate" moment that builds trust.
    // (state declarations live below `finalAmount` since they reference it)

    // List of all active campaigns — always loaded for the dropdown.
    const [campaigns, setCampaigns] = useState([]);
    useEffect(() => {
        api.get("/campaigns/public")
            .then((r) => setCampaigns(r.data || []))
            .catch(() => {});
    }, []);

    const finalAmount = customAmount ? parseFloat(customAmount) : amount;

    // Confirmation step — donor must click "Review my donation" → modal pops
    // up → click Confirm to reveal payment buttons. Any param change resets.
    const [confirmed, setConfirmed] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const previewKey = `${campaignId}|${finalAmount}|${frequency}|${form.donor_name.trim()}|${form.donor_email.trim()}`;
    useEffect(() => { setConfirmed(false); }, [previewKey]);
    const selectedCampaign = campaigns.find((cm) => cm.id === campaignId);

    const handleChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!finalAmount || finalAmount <= 0) {
            toast.error("Please choose a donation amount");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                amount: finalAmount,
                frequency,
                campaign_id: campaignId || null,
                ...form,
                // Carry QR attribution when the donor scanned a flyer.
                ...qrAttributionFields(campaignId || null),
            };
            // Send tribute fields only if a tribute mode is picked
            if (!form.tribute_type) {
                delete payload.tribute_type;
                delete payload.tribute_name;
                delete payload.tribute_notify_name;
                delete payload.tribute_notify_email;
                delete payload.tribute_message;
            } else {
                // Cleanup empty strings for optional fields
                if (!payload.tribute_notify_email) {
                    delete payload.tribute_notify_email;
                    delete payload.tribute_notify_name;
                }
                if (!payload.tribute_message) delete payload.tribute_message;
            }
            const { data } = await api.post("/donations", payload);
            setSuccess(data);
            toast.success("Thank you for your gift!");
        } catch (err) {
            // Use shared error formatter — handles FastAPI validation arrays
            // safely so we never accidentally render an object as JSX (React #31).
            toast.error(formatApiError(err, "Failed to process donation"));
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="donate-success">
                <div className="w-16 h-16 bg-[#F2D8D5] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-8 h-8 text-[#E53935]" fill="#E53935" strokeWidth={2} />
                </div>
                <h1 className="font-heading text-4xl text-[#1C252E] font-medium mb-3">Thank you, {success.donor_name}!</h1>
                <p className="text-[#4A5568] mb-6">
                    Your {success.frequency === "monthly" ? "monthly" : "one-time"} gift of ${success.amount.toFixed(2)} has been received.
                </p>
                {success.matched_amount > 0 && (
                    <div className="bg-gradient-to-r from-[#1C252E] to-[#E53935]/30 text-white rounded-2xl p-5 mb-6 max-w-md mx-auto" data-testid="donate-match-success">
                        <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#E53935] mb-2">2x Match unlocked</p>
                        <p className="font-heading text-2xl font-medium">
                            Your ${success.amount.toFixed(0)} became ${(success.amount + success.matched_amount).toFixed(0)}
                        </p>
                        <p className="text-sm text-white/70 mt-1">+ ${success.matched_amount.toFixed(0)} matched by our sponsor</p>
                    </div>
                )}
                <p className="text-sm text-[#4A5568] mb-10 max-w-md mx-auto">
                    A receipt will be emailed to {success.donor_email}. Every dollar goes directly into family and pet programs.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {/* PDF receipt link only shows when we have a real backend-issued donation id
                        (UUID format). Stripe/PayPal flows return PSP ids like pi_... or paypal —
                        the email receipt is sent automatically; admins can pull the PDF from the
                        Donations panel if needed. */}
                    {success.id && /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(success.id) && (
                        <a
                            href={`${API}/donations/${success.id}/receipt.pdf?email=${encodeURIComponent(success.donor_email.toLowerCase())}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="donate-receipt-pdf-link"
                            className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#1C252E] text-[#1C252E] hover:bg-[#1C252E] hover:text-white rounded-full px-8 py-4 font-medium transition-all"
                        >
                            Download PDF receipt
                        </a>
                    )}
                    <Link
                        to="/"
                        data-testid="donate-success-home-link"
                        className="inline-flex items-center justify-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-8 py-4 font-medium transition-all"
                    >
                        Return home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="donate-page">
            <SEO
                title="Donate · Support family aid & community programs"
                description="Make a one-time or recurring donation to Reddog Community Foundation. Choose the General Support Fund or an earmarked campaign — all gifts tracked publicly on our ledger."
                path="/donate"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "DonateAction",
                    name: "Donate to Reddog Community Foundation",
                    description:
                        "One-time or recurring donations to support family aid, school programs, and community needs. Every dollar is tracked on a public ledger.",
                    recipient: {
                        "@type": "NGO",
                        name: "Reddog Community Foundation Inc.",
                        url: typeof window !== "undefined" ? window.location.origin : "",
                    },
                    target: `${typeof window !== "undefined" ? window.location.origin : ""}/donate`,
                }}
            />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
                <div className="lg:col-span-2">
                    {campaign && (
                        <div className="bg-gradient-to-br from-[#F2D8D5] to-[#FDFBF7] border border-[#E53935]/20 rounded-2xl p-6 mb-6" data-testid="donate-campaign-banner">
                            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-2 flex items-center gap-1.5">
                                <Target className="w-3.5 h-3.5" strokeWidth={2} />
                                Giving to
                            </p>
                            <h3 className="font-heading text-2xl text-[#1C252E] font-medium mb-3">{campaign.name}</h3>
                            <div className="flex items-baseline justify-between mb-1">
                                <p className="text-sm text-[#4A5568]">
                                    <span className="font-bold text-[#1C252E]">${Math.round(campaign.raised).toLocaleString()}</span> of ${Math.round(campaign.goal_amount).toLocaleString()}
                                </p>
                                <p className="text-sm font-bold text-[#E53935]">{Math.round(campaign.percent)}%</p>
                            </div>
                            <div className="h-2 w-full bg-white/70 rounded-full overflow-hidden">
                                <div className="h-full bg-[#E53935] rounded-full" style={{ width: `${Math.min(campaign.percent, 100)}%` }} />
                            </div>
                        </div>
                    )}
                    <Editable contentKey="donate.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                        {campaign ? "Restricted campaign" : "Give & give again"}
                    </Editable>
                    {campaign ? (
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-6" data-testid="donate-campaign-title">
                            <span>Support </span>
                            <br />
                            <span className="text-[#E53935]">{campaign.name}</span>
                        </h1>
                    ) : (
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-6">
                            <Editable contentKey="donate.title1" as="span">Your gift becomes</Editable>
                            <br />
                            <Editable contentKey="donate.title2" as="span">someone's tomorrow.</Editable>
                        </h1>
                    )}
                    {campaign?.description && (
                        <p className="text-base text-[#4A5568] leading-relaxed mb-6" data-testid="donate-campaign-description">
                            {campaign.description}
                        </p>
                    )}
                    {!campaign && (
                    <Editable
                        contentKey="donate.intro"
                        as="p"
                        multiline
                        className="text-lg text-[#4A5568] leading-relaxed mb-6"
                    >
                        Donations are tracked to the selected fund and disbursed only for approved charitable purposes. Reddog Community Foundation Inc. is organized as a nonprofit corporation and intends to apply for recognition as a 501(c)(3) public charity. Donations may not be tax-deductible until IRS recognition is approved.
                    </Editable>
                    )}

                    {/* Two funding types — flexible vs restricted */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="donate-fund-types">
                        <div className="bg-[#F2D8D5] border border-[#E53935]/15 rounded-2xl p-4">
                            <p className="text-xs uppercase tracking-wider font-bold text-[#E53935] mb-1">Reddog General Support Fund</p>
                            <p className="text-xs text-[#4A5568] leading-relaxed">Flexible — used for approved school programs, classroom needs, meals, repairs, field trips, and urgent needs.</p>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                            <p className="text-xs uppercase tracking-wider font-bold text-amber-800 mb-1">Restricted Campaigns</p>
                            <p className="text-xs text-[#4A5568] leading-relaxed">Earmarked to one purpose (e.g. Boiler Repair, Library Fund). Money may only be spent on that purpose.</p>
                        </div>
                    </div>

                    {/* Campaign picker — only shown when nothing is pre-selected.
                        Lists every active fund so donors can explicitly choose
                        where their gift goes (or stay on "General Support" by
                        leaving none picked). */}
                    {!campaign && campaigns.length > 0 && (
                        <div className="mb-10" data-testid="donate-campaign-picker">
                            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#E53935] mb-3">Pick where your gift goes</p>
                            <p className="text-sm text-[#4A5568] mb-4 leading-relaxed">
                                Tap a fund below to direct your donation. Leave none picked to support the flexible General Support Fund.
                            </p>
                            <div className="grid grid-cols-1 gap-3">
                                {campaigns.map((cm) => {
                                    const cmGoal = Number(cm.goal_amount || 0);
                                    const cmRaised = Number(cm.raised || 0);
                                    const cmPct = cmGoal > 0 ? Math.min((cmRaised / cmGoal) * 100, 100) : 0;
                                    const isRestricted = cm.goal_amount > 0;
                                    return (
                                        <Link
                                            key={cm.id}
                                            to={`/donate?campaign=${cm.id}`}
                                            data-testid={`donate-pick-campaign-${cm.slug || cm.id}`}
                                            className="group flex items-stretch gap-4 bg-white border border-black/10 hover:border-[#E53935] rounded-2xl p-3 transition-all hover:-translate-y-0.5"
                                        >
                                            {cm.image_url ? (
                                                <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#F4F1EA] flex-shrink-0">
                                                    <img src={cm.image_url} alt={cm.name} loading="lazy" className="w-full h-full object-contain" />
                                                </div>
                                            ) : (
                                                <div className="w-20 h-20 rounded-xl bg-[#F4F1EA] flex items-center justify-center flex-shrink-0">
                                                    <Heart className="w-7 h-7 text-[#E53935]" strokeWidth={1.5} />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isRestricted ? "bg-amber-50 text-amber-800" : "bg-[#F2D8D5] text-[#E53935]"}`}>
                                                        {isRestricted ? "Restricted" : "Flexible"}
                                                    </span>
                                                </div>
                                                <p className="font-heading text-base text-[#1C252E] font-medium leading-tight mb-1 group-hover:text-[#E53935] transition-colors line-clamp-1">{cm.name}</p>
                                                {isRestricted ? (
                                                    <>
                                                        <div className="h-1.5 w-full bg-[#F4F1EA] rounded-full overflow-hidden mb-1">
                                                            <div className="h-full bg-gradient-to-r from-[#E53935] to-[#D32F2F]" style={{ width: `${cmPct}%` }} />
                                                        </div>
                                                        <p className="text-xs text-[#4A5568]">
                                                            ${Math.round(cmRaised).toLocaleString()} of ${Math.round(cmGoal).toLocaleString()} · {cmPct.toFixed(0)}%
                                                        </p>
                                                    </>
                                                ) : (
                                                    <p className="text-xs text-[#4A5568]">
                                                        ${Math.round(cmRaised).toLocaleString()} raised · supports approved needs
                                                    </p>
                                                )}
                                            </div>
                                            <Target className="w-4 h-4 text-[#4A5568] group-hover:text-[#E53935] self-center flex-shrink-0" strokeWidth={1.5} />
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="space-y-4">
                        {[
                            { amtKey: "donate.tier1_amt", descKey: "donate.tier1_desc" },
                            { amtKey: "donate.tier2_amt", descKey: "donate.tier2_desc" },
                            { amtKey: "donate.tier3_amt", descKey: "donate.tier3_desc" },
                        ].map((t) => (
                            <div key={t.amtKey} className="flex items-start gap-4 bg-[#F4F1EA] rounded-2xl p-5">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                                    <Heart className="w-5 h-5 text-[#E53935]" fill="#E53935" strokeWidth={1.5} />
                                </div>
                                <div className="flex-1">
                                    <Editable contentKey={t.amtKey} as="p" className="font-heading text-lg text-[#1C252E] font-medium">
                                        {c(t.amtKey, "$")}
                                    </Editable>
                                    <Editable contentKey={t.descKey} as="p" className="text-sm text-[#4A5568]">
                                        {c(t.descKey, "")}
                                    </Editable>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-3">
                    <MatchBanner donationAmount={finalAmount || 0} />
                    <BrowserCompatibilityBanner />
                    <form onSubmit={handleSubmit} className="bg-white border border-black/5 rounded-3xl p-6 sm:p-10 space-y-8" data-testid="donate-form">
                        {/* Campaign select — required. Donors MUST pick a fund. The
                            list comes from /api/campaigns/public so the dropdown
                            mirrors whatever's active in admin. URL-supplied
                            campaigns pre-select the right option. */}
                        <div>
                            <label htmlFor="donate-campaign-select" className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] block mb-2">
                                Choose a fund *
                            </label>
                            <select
                                id="donate-campaign-select"
                                value={campaignId}
                                onChange={(e) => setCampaignId(e.target.value)}
                                required
                                data-testid="donate-campaign-select"
                                className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all appearance-none"
                                style={{ backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234A5568' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center", backgroundSize: "1rem", paddingRight: "2.5rem" }}
                            >
                                <option value="">— Select a fund —</option>
                                {campaigns.map((cm) => (
                                    <option key={cm.id} value={cm.id}>
                                        {cm.name}{cm.goal_amount > 0 ? " · restricted" : " · flexible"}
                                    </option>
                                ))}
                            </select>
                            {campaignId && (() => {
                                const sel = campaigns.find((cm) => cm.id === campaignId);
                                if (!sel) return null;
                                const isRestricted = sel.goal_amount > 0;
                                return (
                                    <p className="text-xs text-[#4A5568] mt-2 leading-relaxed">
                                        {isRestricted ? (
                                            <><strong className="text-amber-800">Restricted:</strong> 100% of this gift will be spent on the {sel.name} purpose.</>
                                        ) : (
                                            <><strong className="text-[#E53935]">Flexible:</strong> Goes into the {sel.name} — used for approved school programs, classroom needs, meals, repairs, field trips, and urgent needs.</>
                                        )}
                                    </p>
                                );
                            })()}
                        </div>

                        {/* Frequency toggle */}
                        <div>
                            <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] mb-3">Frequency</p>
                            <div className="flex gap-2" data-testid="donate-frequency-toggle" role="group" aria-label="Donation frequency">
                                {[
                                    { v: "one_time", l: "One-time" },
                                    { v: "monthly", l: "Monthly" },
                                ].map((f) => (
                                    <button
                                        key={f.v}
                                        type="button"
                                        onClick={() => setFrequency(f.v)}
                                        data-testid={`donate-frequency-${f.v}-btn`}
                                        aria-pressed={frequency === f.v}
                                        className={`flex-1 py-3 rounded-full text-sm font-medium transition-all ${
                                            frequency === f.v
                                                ? "bg-[#E53935] text-white shadow-md"
                                                : "bg-[#F4F1EA] text-[#4A5568] hover:bg-[#F2D8D5]"
                                        }`}
                                    >
                                        {f.l}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Amount */}
                        <div>
                            <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] mb-3">Amount (USD)</p>
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3" role="group" aria-label="Donation amount preset">
                                {TIERS.map((t) => (
                                    <button
                                        type="button"
                                        key={t}
                                        onClick={() => { setAmount(t); setCustomAmount(""); }}
                                        data-testid={`donate-tier-${t}-btn`}
                                        aria-pressed={amount === t && !customAmount}
                                        className={`py-3 rounded-xl text-sm font-medium transition-all ${
                                            amount === t && !customAmount
                                                ? "bg-[#1C252E] text-white"
                                                : "bg-white border border-black/10 text-[#1C252E] hover:border-[#E53935]"
                                        }`}
                                    >
                                        ${t}
                                    </button>
                                ))}
                            </div>
                            <input
                                type="number"
                                min="1"
                                step="0.01"
                                placeholder="Custom amount"
                                aria-label="Custom donation amount in US dollars"
                                value={customAmount}
                                onChange={(e) => setCustomAmount(e.target.value)}
                                data-testid="donate-custom-amount-input"
                                className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                            />
                        </div>

                        {/* Donor info */}
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="donate-name" className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] block mb-2">Name *</label>
                                <input
                                    id="donate-name"
                                    name="donor_name"
                                    autoComplete="name"
                                    value={form.donor_name}
                                    onChange={handleChange}
                                    required
                                    data-testid="donate-name-input"
                                    className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                />
                            </div>
                            <div>
                                <label htmlFor="donate-email" className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] block mb-2">Email *</label>
                                <input
                                    id="donate-email"
                                    type="email"
                                    name="donor_email"
                                    autoComplete="email"
                                    value={form.donor_email}
                                    onChange={handleChange}
                                    required
                                    data-testid="donate-email-input"
                                    className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                />
                            </div>
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                name="anonymous"
                                checked={form.anonymous}
                                onChange={handleChange}
                                data-testid="donate-anonymous-checkbox"
                                className="w-4 h-4 accent-[#E53935]"
                            />
                            <span className="text-sm text-[#4A5568]">Make this donation anonymous</span>
                        </label>

                        {/* Tribute / In honor of / In memory of */}
                        <div className="bg-[#FDFBF7] border border-black/10 rounded-2xl p-5" data-testid="donate-tribute-section">
                            <button
                                type="button"
                                onClick={() => setTributeOpen(!tributeOpen)}
                                data-testid="donate-tribute-toggle"
                                aria-expanded={tributeOpen}
                                className="flex items-center justify-between w-full text-left"
                            >
                                <span className="text-sm font-medium text-[#1C252E] flex items-center gap-2">
                                    <Heart className="w-4 h-4 text-[#E53935]" fill={form.tribute_type ? "#E53935" : "none"} strokeWidth={1.5} />
                                    {form.tribute_type ? `In ${form.tribute_type === "honor" ? "honor" : "memory"} of ${form.tribute_name || "…"}` : "Give in honor of someone (optional)"}
                                </span>
                                <span className="text-xs text-[#4A5568]">{tributeOpen ? "Hide" : "Show"}</span>
                            </button>
                            {tributeOpen && (
                                <div className="mt-4 space-y-4">
                                    <div className="flex gap-2" role="group" aria-label="Tribute type">
                                        {[
                                            { v: "", l: "None" },
                                            { v: "honor", l: "In honor of" },
                                            { v: "memory", l: "In memory of" },
                                        ].map((opt) => (
                                            <button
                                                key={opt.v}
                                                type="button"
                                                onClick={() => setForm({ ...form, tribute_type: opt.v })}
                                                data-testid={`donate-tribute-type-${opt.v || "none"}-btn`}
                                                aria-pressed={form.tribute_type === opt.v}
                                                className={`flex-1 py-2 rounded-full text-xs font-medium transition-all ${
                                                    form.tribute_type === opt.v
                                                        ? "bg-[#E53935] text-white"
                                                        : "bg-white border border-black/10 text-[#4A5568] hover:border-[#E53935]"
                                                }`}
                                            >
                                                {opt.l}
                                            </button>
                                        ))}
                                    </div>
                                    {form.tribute_type && (
                                        <>
                                            <div>
                                                <label htmlFor="tribute-name" className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] block mb-2">
                                                    {form.tribute_type === "honor" ? "In honor of *" : "In memory of *"}
                                                </label>
                                                <input
                                                    id="tribute-name"
                                                    type="text"
                                                    name="tribute_name"
                                                    value={form.tribute_name}
                                                    onChange={handleChange}
                                                    required={!!form.tribute_type}
                                                    maxLength={120}
                                                    placeholder="e.g. Mrs. Smith, our 3rd grade teacher"
                                                    data-testid="donate-tribute-name-input"
                                                    className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                                />
                                            </div>
                                            <p className="text-xs text-[#4A5568]">
                                                Optionally notify the family — we'll send them a tasteful card-style email (no dollar amount shown).
                                            </p>
                                            <div className="grid sm:grid-cols-2 gap-3">
                                                <input
                                                    type="text"
                                                    name="tribute_notify_name"
                                                    value={form.tribute_notify_name}
                                                    onChange={handleChange}
                                                    maxLength={120}
                                                    placeholder="Recipient name (optional)"
                                                    aria-label="Notification recipient name"
                                                    data-testid="donate-tribute-notify-name-input"
                                                    className="rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                                />
                                                <input
                                                    type="email"
                                                    name="tribute_notify_email"
                                                    value={form.tribute_notify_email}
                                                    onChange={handleChange}
                                                    placeholder="their@email.com (optional)"
                                                    aria-label="Notification recipient email"
                                                    data-testid="donate-tribute-notify-email-input"
                                                    className="rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                                />
                                            </div>
                                            <textarea
                                                name="tribute_message"
                                                value={form.tribute_message}
                                                onChange={handleChange}
                                                rows={2}
                                                maxLength={600}
                                                placeholder="Personal message (optional)"
                                                aria-label="Personal tribute message"
                                                data-testid="donate-tribute-message-input"
                                                className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] resize-none"
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Payment section — gated on amount, name+email, campaign, AND
                            donor having clicked the confirm modal. Donors first see a
                            "Review my donation" button → modal with summary → Confirm →
                            payment buttons appear. */}
                        {finalAmount > 0 && form.donor_name.trim() && form.donor_email.trim() && campaignId ? (
                            confirmed ? (
                                <>
                                <div data-testid="donate-paypal-section" className="space-y-3">
                                    <PayPalCheckoutButtons
                                        purpose="donation"
                                        amount={finalAmount}
                                        campaignId={campaignId || undefined}
                                        donorName={form.donor_name}
                                        donorEmail={form.donor_email}
                                        description={`Reddog Foundation donation${frequency === "monthly" ? " (one-time; for monthly, see backend subscription endpoint)" : ""}`}
                                        onSuccess={(result) => {
                                            // PayPal's capture handler already inserted the donation
                                            // row + auto-posted the JE. We must NOT also POST to
                                            // /api/donations — that's the legacy mock endpoint and
                                            // creates a duplicate row. Just show the success state.
                                            setSuccess({
                                                id: result?.order_id || result?.capture_id || "paypal",
                                                donor_email: form.donor_email,
                                                donor_name: form.donor_name,
                                                amount: finalAmount,
                                                frequency,
                                                campaign_id: campaignId || null,
                                                source: "paypal",
                                            });
                                            toast.success("Thank you for your gift!");
                                        }}
                                    />
                                    {/* Powered-by caption — only shown when PayPal is actually live. */}
                                    {c("paypal.disabled", "false") !== "true" && (
                                        <p className="text-[11px] text-[#4A5568] text-center">Powered by PayPal — credit card, debit card, Venmo, PayPal balance, and bank transfer all accepted.</p>
                                    )}
                                </div>

                                {/* Stripe Elements — card-only.
                                    Renders below PayPal as a card-only fallback. */}
                                <div data-testid="donate-stripe-section" className="space-y-3 pt-2">
                                    {c("paypal.disabled", "false") !== "true" && (
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 h-px bg-black/10" />
                                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
                                                Or pay with card
                                            </p>
                                            <div className="flex-1 h-px bg-black/10" />
                                        </div>
                                    )}
                                    <StripePaymentForm
                                        purpose="donation"
                                        amount={finalAmount}
                                        campaignId={campaignId || undefined}
                                    donorName={form.donor_name}
                                    donorEmail={form.donor_email}
                                    monthly={frequency === "monthly"}
                                    onSuccess={(result) => {
                                        // Stripe is its own source of truth — the backend already
                                        // inserted the donation row via /confirm-payment + webhook
                                        // and auto-posted the journal entry. Do NOT also POST to
                                        // /api/donations (that's the legacy mock endpoint and
                                        // creates a duplicate row).
                                        setSuccess({
                                            id: result?.payment_intent_id || "stripe",
                                            donor_email: form.donor_email,
                                            donor_name: form.donor_name,
                                            amount: finalAmount,
                                            frequency,
                                            campaign_id: campaignId || null,
                                            source: "stripe",
                                        });
                                    }}
                                />
                                </div>
                            </>
                            ) : (
                                /* All inputs valid but donor hasn't confirmed yet.
                                   Show big red "Review my donation" button that
                                   opens the confirmation modal. */
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmModal(true)}
                                    data-testid="donate-review-btn"
                                    className="w-full inline-flex items-center justify-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-8 py-4 font-medium text-base transition-all"
                                >
                                    <Heart className="w-4 h-4" strokeWidth={1.5} />
                                    Review my {frequency === "monthly" ? "monthly " : ""}donation — ${finalAmount.toFixed(2)}
                                </button>
                            )
                        ) : finalAmount > 0 ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900" data-testid="donate-need-contact-info">
                                {!campaignId
                                    ? <>Please <strong>choose a fund</strong> above to continue.</>
                                    : <>Please enter your <strong>name</strong> and <strong>email</strong> above to continue. We need these to send your receipt.</>}
                            </div>
                        ) : null}

                        {/* Confirmation modal — surfaced after "Review my donation".
                            Shows a clean summary of campaign, amount, frequency,
                            name, email so the donor sees exactly what they're
                            about to do. Cancel → close. Confirm → unlock the
                            payment buttons (PayPal / Stripe card). */}
                        {showConfirmModal && (
                            <DonateConfirmModal
                                campaign={selectedCampaign}
                                amount={finalAmount}
                                frequency={frequency}
                                donorName={form.donor_name}
                                donorEmail={form.donor_email}
                                onCancel={() => setShowConfirmModal(false)}
                                onConfirm={() => {
                                    setConfirmed(true);
                                    setShowConfirmModal(false);
                                }}
                            />
                        )}

                        {/* "Or record an offline pledge (check by mail)" — hidden
                            for now; mail-pledge flow is backlogged. */}

                        <p className="text-xs text-[#4A5568] text-center flex items-center justify-center gap-2">
                            <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                            <Editable contentKey="donate.secure_note" as="span">Secure PayPal donations</Editable>
                        </p>
                    </form>

                    {/* Volunteer prompt */}
                    <div className="bg-white border border-black/5 rounded-3xl p-6 sm:p-10 mt-6">
                        <VolunteerForm campaignId={campaignId} compact />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Donate;

// Confirmation modal shown after donor clicks "Review my donation". Surfaces
// a clean summary of WHAT they're about to pay before any payment buttons
// render. Returns to the form via Cancel; unlocks payment buttons via Confirm.
const DonateConfirmModal = ({ campaign, amount, frequency, donorName, donorEmail, onCancel, onConfirm }) => {
    const isRestricted = !!(campaign && campaign.goal_amount > 0);
    return (
        <div
            data-testid="donate-confirm-modal"
            className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
            onClick={onCancel}
            role="dialog"
            aria-modal="true"
            aria-label="Confirm your donation"
        >
            <div
                className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-[#FAF6EE] flex items-center justify-center">
                        <Heart className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-2xl text-[#1C252E]">Confirm your gift</h3>
                </div>

                <dl className="space-y-3 mb-6">
                    <div className="flex justify-between items-baseline gap-4 pb-3 border-b border-black/5">
                        <dt className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Amount</dt>
                        <dd className="font-heading text-2xl text-[#E53935]" data-testid="confirm-modal-amount">
                            ${amount.toFixed(2)}{frequency === "monthly" ? " / month" : ""}
                        </dd>
                    </div>
                    <div className="flex justify-between items-baseline gap-4">
                        <dt className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Fund</dt>
                        <dd className="text-sm text-[#1C252E] font-medium text-right" data-testid="confirm-modal-campaign">
                            {campaign?.name || "—"}
                            {isRestricted && <span className="block text-[10px] uppercase tracking-wider font-bold text-purple-700 mt-0.5">Restricted · 100% to this purpose</span>}
                        </dd>
                    </div>
                    <div className="flex justify-between items-baseline gap-4">
                        <dt className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Frequency</dt>
                        <dd className="text-sm text-[#1C252E]">{frequency === "monthly" ? "Monthly (recurring)" : "One-time"}</dd>
                    </div>
                    <div className="flex justify-between items-baseline gap-4">
                        <dt className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Donor</dt>
                        <dd className="text-sm text-[#1C252E] text-right">
                            {donorName}
                            <span className="block text-xs text-[#4A5568]">{donorEmail}</span>
                        </dd>
                    </div>
                </dl>

                <p className="text-xs text-[#4A5568] mb-5 leading-relaxed">
                    Clicking <strong>Confirm</strong> unlocks the payment options below. You&apos;ll still pick PayPal or card and enter your payment details — nothing is charged yet.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="confirm-modal-cancel-btn"
                        className="flex-1 px-6 py-3 rounded-full border border-black/10 text-[#1C252E] hover:border-black/30 transition-colors font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        data-testid="confirm-modal-confirm-btn"
                        className="flex-1 px-6 py-3 rounded-full bg-[#E53935] text-white hover:bg-[#D32F2F] transition-colors font-medium"
                    >
                        Confirm &amp; continue
                    </button>
                </div>
            </div>
        </div>
    );
};

// Proactive heads-up shown to Firefox + Brave visitors before they start
// filling out the form, since those browsers' default privacy settings will
// often block the Stripe card iframe entirely. We only render when the user
// hasn't dismissed it in this browser. PayPal still works in all browsers.
const BrowserCompatibilityBanner = () => {
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem("donate.browser_banner_dismissed") === "1"; } catch { return false; }
    });
    const browser = detectBrowser();
    const risky = isStripeRiskyBrowser();
    if (dismissed || !risky) return null;

    const dismiss = () => {
        setDismissed(true);
        try { localStorage.setItem("donate.browser_banner_dismissed", "1"); } catch { /* ignore */ }
    };

    // Browser-specific shield-icon instructions
    const fix = browser.name === "firefox"
        ? "Click the shield icon (left of the URL bar) → toggle off Enhanced Tracking Protection for this site → reload."
        : browser.name === "brave"
        ? "Click the Brave shield (orange lion icon, right of the URL bar) → set Shields to DOWN for this site → reload."
        : "Disable strict privacy / tracker-blocking for this site, then reload.";

    return (
        <div
            data-testid="browser-compat-banner"
            className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4 text-sm text-blue-900 flex items-start gap-3"
        >
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-700" strokeWidth={2} />
            <div className="flex-1 space-y-1.5">
                <p className="font-bold">
                    Using {browser.name === "firefox" ? "Firefox" : browser.name === "brave" ? "Brave" : "this browser"}? Card payments may be blocked.
                </p>
                <p className="leading-relaxed">
                    Your privacy settings may prevent Stripe&apos;s secure card form from loading. <strong>PayPal donations always work</strong> (it accepts debit / credit cards too — no PayPal account needed). To use the card form directly: {fix}
                </p>
                <p className="text-xs text-blue-800">
                    Or switch to <a href="https://www.google.com/chrome/" target="_blank" rel="noreferrer" className="underline font-medium">Chrome</a> · <a href="https://www.microsoft.com/edge" target="_blank" rel="noreferrer" className="underline font-medium">Edge</a> · Safari — Stripe works out of the box.
                </p>
            </div>
            <button
                onClick={dismiss}
                aria-label="Dismiss"
                data-testid="browser-compat-banner-dismiss"
                className="flex-shrink-0 p-1 rounded-full hover:bg-blue-100 transition-colors"
            >
                <X className="w-4 h-4 text-blue-700" />
            </button>
        </div>
    );
};
